import { Typography } from '@braskai/ui-kit';

import { Stack } from '@mui/material';

import { TranslationKey, useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceCurrency } from '@/shared/i18n/formatLocale';
import { BoosterType, Plan, UserBoosters } from '@/shared/types/Billing';

type BoosterWithPrice = { formattedPrice: string; price: number } & Plan;

type RemovedBoostersListProps = {
  allBoosters: null | Partial<Record<BoosterType, BoosterWithPrice>>;
  purchasedBoosters: null | Partial<UserBoosters>;
  selectedBoosters: Record<string, { isSelected: boolean; quantity: number }>;
};

const BOOSTER_TITLE_KEYS: Record<BoosterType, TranslationKey> = {
  [BoosterType.SRT]: 'boosters.item.srt.title',
  [BoosterType.TeamspaceSeat]: 'boosters.item.seat.title',
  [BoosterType.TranslationPrompting]: 'boosters.item.prompting.title',
};

export const RemovedBoostersList = ({ allBoosters, purchasedBoosters, selectedBoosters }: RemovedBoostersListProps) => {
  const { locale, t } = useInterfaceLanguage();

  if (!allBoosters || !purchasedBoosters) return null;

  const removedBoosters = Object.entries(purchasedBoosters)
    .map(([type]) => {
      const boosterType = type as BoosterType;
      const isSelected = selectedBoosters[boosterType]?.isSelected;
      const booster = allBoosters[boosterType];

      if (!isSelected && booster) {
        return { booster, type };
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (removedBoosters.length === 0) return null;

  return (
    <Stack mt={16}>
      {removedBoosters.map(({ booster, type }) => (
        <Stack key={`${type}-remove`} alignItems="center" direction="row" justifyContent="space-between">
          <Typography color="grey.700" variant="body-2">
            {t('billing.confirm.removeItem', { item: t(BOOSTER_TITLE_KEYS[type as BoosterType]) })}
          </Typography>
          <Typography color="grey.700" variant="body-2">
            {formatInterfaceCurrency(locale, 0, booster.prices[0]?.currency ?? 'usd')}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
